import { useId } from 'react';
import { useRadioGroup } from 'react-aria';
import { useRadioGroupState } from 'react-stately';

import { CloseButton } from '../buttons/CloseButton';
import { GradientButton } from '../buttons/GradientButton';
import { Dialog } from '../overlay/Dialog';

import { webAppSettingsStore } from '../../store/web-app-settings/WebAppSettingsStore';

import { useI18n } from '../../hooks/useI18nFormatters';
import { useWebAppSettings } from '../../store/web-app-settings/useWebAppSettings';

import styles from './AppearanceDialog.module.css';

import { type Appearance, AppearanceOption } from './AppearanceOption';

interface AppearanceDialogProps {
  onClose: () => void;
}

/**
 * Centered dialog to choose between the light and dark appearance. A choice applies right away.
 */
export function AppearanceDialog(props: AppearanceDialogProps) {
  const { i18n } = useI18n();
  const { settings } = useWebAppSettings();
  const titleId = useId();

  const selectedAppearance: Appearance = settings.theme.darkMode === true ? 'dark' : 'light';
  const radioGroupState = useRadioGroupState({
    value: selectedAppearance,
    onChange: handleAppearanceChange,
  });
  const radioGroup = useRadioGroup(
    {
      orientation: 'horizontal',
      'aria-labelledby': titleId,
    },
    radioGroupState,
  );

  const title = i18n.messageFormatter('navigation.profile.appearance');

  function handleAppearanceChange(value: string) {
    webAppSettingsStore.setDarkMode(value === 'dark');
  }

  return (
    <Dialog aria-label={title} onClose={props.onClose}>
      <div className={styles.root}>
        <div className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>

          <CloseButton className={styles.closeButton} onPress={props.onClose} />
        </div>

        <div {...radioGroup.radioGroupProps} className={styles.options}>
          <AppearanceOption
            value={'light'}
            label={i18n.messageFormatter('navigation.profile.appearanceLight')}
            state={radioGroupState}
          />

          <AppearanceOption
            value={'dark'}
            label={i18n.messageFormatter('navigation.profile.appearanceDark')}
            state={radioGroupState}
          />
        </div>

        <div className={styles.footer}>
          <GradientButton className={styles.doneButton} styleWidth={'full'} onPress={props.onClose}>
            {i18n.messageFormatter('common.done')}
          </GradientButton>
        </div>
      </div>
    </Dialog>
  );
}
